/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {useCallback, useEffect} from 'react'
import {Form, FormDataContext} from '@modifi/plugin-forms'
import {Col, DangerAlert, InlineLoader, Row, SuccessAlert} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'

import {RegistrationNumbersFormContent} from './components/registration-numbers-form-content'
import {ConnectedFormFooter} from '../../../plugins/onboarding/components/form-cta-footer'
import {FormContentWrapper} from '../../../ui/form/styles'
import SummaryPageFooter from '../components/summary-page-footer'
import {SECTION_REGISTRATION_NUMBERS} from '../../../constants/sections'
import {CompanyUnlockRefresher} from '../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchCompanyRegistrationByExternalId} from '../../../data/company-registration'

export type RegistrationNumbersProps = {
  initialFormData?: {
    registrationNumber?: string
    registrationAuthority?: string
    taxNumber?: string
    vatNumber?: string
    importExportCode?: string
  }
  onSubmit: (externalCompanyId: string | null | undefined, changes: Record<string, unknown>) => void
  onDiscard: () => void
  initialFormDataLoading?: boolean
  updatePending: boolean
  errorMessage?: string | null
  externalErrorMessages?: Record<string, string>
  initialize: (externalCompanyId: string) => void
  externalCompanyId?: string | null
  pageIsVerified: boolean
}

const RegistrationNumbers: React.FC<RegistrationNumbersProps> = ({
  externalErrorMessages = {},
  errorMessage = null,
  onSubmit,
  onDiscard,
  initialFormData,
  initialFormDataLoading = true,
  updatePending,
  initialize,
  externalCompanyId,
  pageIsVerified,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])

  useEffect(() => {
    if (externalCompanyId) {
      initialize(externalCompanyId)
    }
  }, [initialize, externalCompanyId])

  const handleSubmit = useCallback(
    (changes: Record<string, unknown>) => {
      onSubmit(externalCompanyId, changes)
    },
    [onSubmit, externalCompanyId]
  )

  if (initialFormDataLoading) {
    return (
      <Row justifyContent="center">
        <Col auto>
          <InlineLoader />
        </Col>
      </Row>
    )
  }
  return (
    <Form
      isLoading={updatePending}
      initialValues={initialFormData}
      onSubmit={handleSubmit}
      onDiscard={onDiscard}
    >
      <FormContentWrapper>
        {pageIsVerified && (
          <SuccessAlert>{t('company-registration-numbers-page.fully-verified-alert')}</SuccessAlert>
        )}
        {errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}

        <RegistrationNumbersFormContent externalErrorMessages={externalErrorMessages || {}} />
      </FormContentWrapper>

      <CompanyUnlockRefresher
        refetch={(): any[] => [aFetchCompanyRegistrationByExternalId()]}
        initialValues={initialFormData}
      />
      <ConnectedFormFooter loading={updatePending} hasError={!!errorMessage} />
      <FormDataContext.Consumer>
        {({hasChanges}) =>
          !hasChanges && <SummaryPageFooter sectionName={SECTION_REGISTRATION_NUMBERS} />
        }
      </FormDataContext.Consumer>
    </Form>
  )
}

export default RegistrationNumbers
